import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { SessionService } from '../../services/session.service';
import type { MlClaimReputationRow, MlClaimsReputationReport } from '../../models/api.models';

@Component({
  selector: 'app-reputation-dashboard-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './reputation-dashboard-page.component.html',
  styleUrl: './reputation-dashboard-page.component.css'
})
export class ReputationDashboardPageComponent {
  readonly startDate = signal('');
  readonly endDate = signal('');
  readonly loading = signal(false);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly message = signal<string | null>(null);
  readonly report = signal<MlClaimsReputationReport | null>(null);
  readonly syncProgress = signal<{ processed: number; total: number } | null>(null);

  readonly busy = computed(() => this.loading() || this.syncing());
  readonly periodLabel = computed(() => `${this.formatDate(this.startDate())} até ${this.formatDate(this.endDate())}`);

  constructor(
    private readonly api: ApiService,
    private readonly session: SessionService
  ) {
    this.setDefaultPeriod();
    void this.bootstrap();
  }

  async bootstrap(): Promise<void> {
    const ok = await this.session.refresh();
    if (!ok) {
      this.error.set('Acesso não autenticado. Entre novamente pelo Cloudflare Access.');
      return;
    }
    await this.loadReport();
  }

  setDefaultPeriod(): void {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.startDate.set(this.toDateInput(start));
    this.endDate.set(this.toDateInput(end));
  }

  setLast30Days(): void {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 30);
    this.startDate.set(this.toDateInput(start));
    this.endDate.set(this.toDateInput(end));
    void this.loadReport();
  }

  async loadReport(): Promise<void> {
    if (!this.startDate() || !this.endDate()) return;
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);
    try {
      const report = await this.api.getMlClaimsReputationReport(this.startDate(), this.endDate());
      this.report.set(report);
      this.message.set(
        report.totals.lastSyncedAt
          ? `Dados carregados do banco. Última atualização: ${this.formatDateTime(report.totals.lastSyncedAt)}.`
          : 'Ainda não há dados salvos para este período. Clique em “Atualizar pela API”.'
      );
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao carregar relatório de reputação.');
      this.report.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  async syncReport(): Promise<void> {
    if (!this.startDate() || !this.endDate()) return;
    this.syncing.set(true);
    this.error.set(null);
    this.message.set('Atualizando termômetro oficial do vendedor no Mercado Livre...');
    this.syncProgress.set({ processed: 0, total: 0 });
    try {
      await this.api.syncMlSellerReputation();
      this.message.set('Buscando devoluções, reclamações e impacto de reputação em lotes...');
      let offset = 0;
      let total = 0;
      const limit = 3;
      const errors: string[] = [];

      for (let guard = 0; guard < 500; guard += 1) {
        const step = await this.api.syncMlClaimsReputationStep(this.startDate(), this.endDate(), offset, limit);
        total = step.total;
        const processed = step.done ? total : Math.min(total, step.nextOffset ?? offset + step.processed);
        this.syncProgress.set({ processed, total });
        this.message.set(`Conferindo reclamações/devoluções: ${processed} de ${total || '?'}.`);
        if (step.errors.length) errors.push(...step.errors);
        if (step.done || step.nextOffset === null) break;
        offset = step.nextOffset;
      }

      const report = await this.api.getMlClaimsReputationReport(this.startDate(), this.endDate());
      this.report.set(report);
      this.message.set(errors.length ? `Atualizado, mas ${errors.length} detalhe(s) precisaram ficar para conferência manual.` : 'Relatório atualizado com dados oficiais do Mercado Livre.');
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao atualizar reputação/devoluções pela API.');
    } finally {
      this.syncing.set(false);
      this.syncProgress.set(null);
    }
  }

  reputationLevelLabel(value: string | null | undefined): string {
    const normalized = String(value ?? '').toLowerCase();
    const map: Record<string, string> = {
      '5_green': 'Verde escuro',
      '4_light_green': 'Verde claro',
      '3_yellow': 'Amarelo',
      '2_orange': 'Laranja',
      '1_red': 'Vermelho'
    };
    return map[normalized] ?? (value || 'Não informado');
  }

  powerSellerLabel(value: string | null | undefined): string {
    const normalized = String(value ?? '').toLowerCase();
    const map: Record<string, string> = {
      platinum: 'MercadoLíder Platinum',
      gold: 'MercadoLíder Gold',
      silver: 'MercadoLíder Silver',
      none: 'Sem selo MercadoLíder'
    };
    return map[normalized] ?? (value || 'Não informado');
  }

  formatPercent(value: number | null | undefined): string {
    return `${(Number(value ?? 0) * 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}%`;
  }

  reputationLabel(value: string | null | undefined): string {
    const normalized = String(value ?? '').toLowerCase();
    if (normalized === 'affected') return 'Afetou reputação';
    if (normalized === 'not_affected') return 'Não afetou';
    if (normalized === 'not_applies') return 'Não se aplica';
    if (normalized === 'has_incentive') return 'Ainda dá para evitar';
    return 'Não informado';
  }

  reputationClass(value: string | null | undefined): string {
    const normalized = String(value ?? '').toLowerCase();
    if (normalized === 'affected') return 'danger';
    if (normalized === 'has_incentive') return 'warning';
    if (normalized === 'not_affected') return 'ok';
    return 'neutral';
  }

  statusLabel(value: string | null | undefined): string {
    const map: Record<string, string> = {
      opened: 'Aberta',
      reopened: 'Reaberta',
      closed: 'Fechada',
      in_mediation: 'Em mediação',
      canceled: 'Cancelada'
    };
    const normalized = String(value ?? '').toLowerCase();
    return map[normalized] ?? (value || '-');
  }

  claimTitle(claim: MlClaimReputationRow): string {
    if (!claim.itemTitle && claim.resource === 'shipment') return `Envio ${claim.resourceId || claim.orderId || claim.claimId}`;
    return claim.itemTitle || (claim.orderId ? `Pedido ${claim.orderId}` : `Reclamação ${claim.claimId}`);
  }

  claimReference(claim: MlClaimReputationRow): string {
    if (claim.orderId) return `Pedido ${claim.orderId}`;
    if (claim.resource === 'shipment') return `Envio ${claim.resourceId || claim.claimId}`;
    if (claim.resource && claim.resourceId) return `${claim.resource} ${claim.resourceId}`;
    return '-';
  }

  claimAmountLabel(claim: MlClaimReputationRow): string {
    if (Number(claim.amount ?? 0) !== 0) return this.formatMoney(claim.amount);
    if (claim.resource === 'shipment' && !claim.itemId) return 'Sem valor na claim';
    return this.formatMoney(claim.amount);
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const [year, month, day] = value.slice(0, 10).split('-');
    return year && month && day ? `${day}/${month}/${year}` : value;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const cleaned = value.replace('T', ' ').replace(/\.\d{3}.*$/, '').replace(/\+.*$/, '').trim();
    const [datePart, timePart] = cleaned.split(' ');
    return `${this.formatDate(datePart)}${timePart ? ' ' + timePart.slice(0, 5) : ''}`;
  }

  formatMoney(value: number | null | undefined): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value ?? 0));
  }

  logout(): void {
    window.location.href = '/cdn-cgi/access/logout?returnTo=' + encodeURIComponent(window.location.origin);
  }

  private toDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
